import React, { useContext, useEffect, useState } from "react";
import {
  Activity,
  ArrowLeft,
  Building,
  Calendar,
  Edit,
  HardDrive,
  Mail,
  Phone,
  Trash2,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import { Input } from "../../../../components/ui/input";
import {
  deleteEmployee,
  deleteUsers,
  getAllEmployee,
  setUserInformation,
  UpdateUserRole,
  UpdateUserStatus,
} from "../../../../services/apiservices/employee";
import { Button } from "../../../../components/ui/button";
import moment from "moment";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../../../components/ui/alertDialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../../../components/ui/dialog";
import { useToast } from "../../../../hooks/useToast";
import { Switch } from "../../../../components/ui/switch";
import { forgotPasswordUser } from "../../../../services/firebase";
import { generateP, packageAccess } from "../../../../services/helpers";
import CommonContext from "../../../../hooks/commonContext";
import { getCompanyStorageData } from "../../../../services/apiservices/messages";
import { Progress } from "../../../../components/ui/progrss";
import { handleEmailChangeSuperUser } from "../../../../services/apiservices/email";

const formatStorage = (bytes) => {
  if (bytes < 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
  if (bytes >= 1 * 1024 * 1024 * 1024 * 1024) {
    return `${(bytes / (1 * 1024 * 1024 * 1024 * 1024)).toFixed(2)} TB`;
  }
  if (bytes >= 1024 * 1024 * 1024) {
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const StorageUsageGauge = ({ 
  used, 
  total, 
  percentage 
}) => {
  const getProgressColor = () => {
    if (percentage > 90) return "bg-red-500";
    if (percentage > 75) return "bg-yellow-500";
    return "bg-green-500";
  };

  return (
    <div className="w-full space-y-2">
      <div className="flex justify-between items-center mb-4">
        <span className="text-sm font-medium">
          {formatStorage(used)} of {formatStorage(total)} used
        </span>
        <span className="text-sm text-gray-500">
          {percentage < 0.01 ? "0.01%" : `${percentage.toFixed(2)}%`}
        </span>
      </div>
      <Progress
        value={percentage} 
        className="h-2 bg-gray-200 rounded-full"
        indicatorClassName={getProgressColor()}
      />
    </div>
  );
};

const UserDetails = () => {
  const [userDetails, setUserDetails] = useState();
  const [resetPasswordDialog, setResetPasswordDialog] = useState(false);
  const [changeRoleDialog, setChangeRoleDialog] = useState(false);
  const [editInformationDailog, setEditInformationDailog] = useState(false);
  const [storageData, setStorageData] = useState({totalStorage: null, percentage: null, usedStorage: null, images: null, document: null, others: null });
  const [selectedRole, setSelectedRole] = useState("");
  const [isActive, setIsActive] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const commonContext = useContext(CommonContext);
  const [formData, setFormData] = useState({});

  const { userId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const ALL_OPTIONS = ["Admin", "Client Team", "Guest", "Support"];

  const getRoleOptions = (role) => {
    return ALL_OPTIONS?.filter((opt) => opt !== role);
  };

  const fetchUserDetails = async () => {
    commonContext?.setLoader(true);
    const [userRes] = await Promise.all([
      new Promise((resolve, reject) => getAllEmployee(resolve, reject)),
    ]);
    
    const userData = userRes?.data?.filter((item) => item?.id === userId);
    const storageRes = await getCompanyStorageData(userData?.companyId);

    const newData = userData?.map((item) => {
      const companyData = userRes?.data?.find(
        (ind) => ind?.id === item?.companyId
      );
      return {
        ...item,
        name: `${item.firstName} ${item.lastName}`,
        email: item.email,
        newRole: (Array.isArray(item?.role) ? item.role.includes("Employee") : item?.role === "Employee") ? item?.employeerole === "Admin" ? "Team Admin" : item?.employeerole : item?.role,
        contact: item?.phoneNumber ? item?.phoneNumber : item?.phone,
        type:
          (Array.isArray(item?.role) ? item.role.includes("Employee") : item?.role === "Employee") && item?.employeerole === "Client Team"
            ? "mobile"
            : "web",
        // company: `${companyData?.firstName} ${companyData?.lastName}`,
        company: companyData?.companyName,
        companyId: companyData?.id,
        createdAt: moment(item?.createdAt).format("MMMM D, YYYY"),
        subscriptions: companyData?.subscriptions
      };
    });
    const FREE = process.env.REACT_APP_CUSTOM_ENV === "dev" ? "prod_Sk8QqRuvoQLkQK" : "prod_Sk8YQ7MHqCIMSV";
    const product = newData?.[0]?.subscriptions?.data?.length > 0 ? newData?.[0]?.subscriptions?.data?.[0]?.plan?.product : FREE;
    const allowedPermissions = packageAccess(product);
    const extraLimit =  newData?.[0]?.subscriptions?.additionalQuantity ? newData?.[0]?.subscriptions?.additionalQuantity * 5 : 0 ;
    const extraLimitInBytes = extraLimit * 1024 ** 3;
    const storageLimit = (allowedPermissions?.storageLimits ?? 0) + extraLimitInBytes;
    const usedPercentage = (storageRes?.totalSize / storageLimit) * 100;
    setStorageData({totalStorage: storageLimit, usedStorage: storageRes?.totalSize, percentage: usedPercentage, images: storageRes?.images, document: storageRes?.documents, others: storageRes?.others});
    
    const roleOptions =
      (Array.isArray(newData?.[0]?.role) ? newData?.[0]?.role.includes("Employee") : newData?.[0]?.role === "Employee")
        ? getRoleOptions(newData?.[0]?.newRole)
        : [];
    setRoleOptions(roleOptions);
    setIsActive(newData?.[0]?.status);
    setUserDetails(newData?.[0]);
    commonContext?.setLoader(false);
  };

  useEffect(() => {
    fetchUserDetails();
  }, []);

  const handleResetPassword = async () => {
    const email = userDetails?.email;
    await forgotPasswordUser(email, () => {});
    toast({
      title: "Password Reset Email Sent",
      description: `A password reset email has been sent to ${email}`,
    });
    setResetPasswordDialog(false);
    fetchUserDetails();
  };

  const handleOpenEdit = () => {
    setFormData({
      firstName: userDetails?.firstName || "",
      lastName: userDetails?.lastName || "",
      email: userDetails?.email || "",
      contact: userDetails?.contact || "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpdateInformation = async () => {
    try {
      setEditInformationDailog(false);
      commonContext?.setLoader(true);

      let mobileChange = false;

      if(userDetails?.contact !== formData?.contact) {
        mobileChange = true;
      }

      const userId = userDetails?.id;
      const data = {
        firstName: formData?.firstName,
        lastName: formData?.lastName,
        phoneNumber: formData?.contact,
      };

      if (userDetails?.email !== formData?.email) {
        data.pendingEmail = formData?.email;
        data.email = userDetails?.email;

        handleEmailChangeSuperUser(userDetails?.email, formData?.email, data?.firstName, userId);
      } else {
        data.email = userDetails?.email;
      }

      const result = await setUserInformation(userId, data, mobileChange);

      if (result.status === true) {
        fetchUserDetails?.();
        toast({
          title: "Account Updated",
          description: `${userDetails?.name}'s account has been updated successfully.`,
        });
      } else if (result.field === "phone") {
        toast({
          title: "Account Updated Error",
          description: "This phone number is already in use by another account.",
          variant: "destructive",
        });
        setFormData({
          firstName: formData?.firstName || "",
          lastName: formData?.lastName || "",
          email: formData?.email || "",
          contact: formData?.contact || "",
        });
        setEditInformationDailog(true);
      } else {
        toast({
          title: "Update Failed",
          description: result.message || "Something went wrong while updating the account.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error updating information:", error);
      toast({
        title: "Update Failed",
        description: error.message || "Something went wrong while updating the account.",
        variant: "destructive",
      });
    } finally {
      commonContext?.setLoader(false);
    }
  };

  const handleDeleteUser = () => {
    commonContext?.setLoader(true);
    const id = userDetails?.id;
    const role = userDetails?.role;
    const email = userDetails?.email;

    if (!id || !role) {
      toast({
        title: "Error",
        description: "Missing user ID or role.",
        variant: "destructive",
      });
      return;
    }

    const onSuccess = () => {
      toast({
        title: "Account Deleted",
        description: `${userDetails?.name}'s account has been deleted`,
        variant: "destructive",
      });
      commonContext?.setLoader(false);
      fetchUserDetails?.();
    };

    const onError = (err) => {
      toast({
        title: "Delete Failed",
        description:
          err?.error || "Something went wrong while deleting the user",
        variant: "destructive",
      });
      commonContext?.setLoader(false);
    };

    if ((Array.isArray(userDetails?.role) ? userDetails.role.includes("Employee") : userDetails?.role === "Employee")) {
      deleteEmployee(id, { email: userDetails?.email }, onSuccess, onError);
    } else {
      deleteUsers(id)
        .then(() => onSuccess())
        .catch((err) => onError({ error: err.message }));
    }
  };

  const handleRoleChange = async () => {
    if (!selectedRole || !userDetails) return;

    const tempPassword = generateP();

    const formData = {
      employeerole: selectedRole,
      email: userDetails?.email,
      password: tempPassword,
      firstname: userDetails?.firstname || userDetails?.name || "User",
      phoneNumber: userDetails?.phoneNumber || "",
    };

    try {
      await UpdateUserRole(userDetails.id, formData, userDetails?.companyId);

      toast({
        title: "Role Updated",
        description: `User role has been updated to ${selectedRole}`,
      });

      setChangeRoleDialog(false);
      fetchUserDetails();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update user role. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleToggleStatus = async () => {
    if (userDetails?.role === "Client") {
      toast({
        title: "Error",
        description: `You cannot deactivate ${userDetails?.name}`,
        variant: "destructive",
      });
      return;
    }

    try {
      const newStatus = !isActive;
      setIsActive(newStatus);
      await UpdateUserStatus(userDetails?.id, newStatus);

      toast({
        title: `User ${newStatus ? "Activated" : "Deactivated"}`,
        description: `${userDetails?.name} has been ${
          newStatus ? "activated" : "deactivated"
        }`,
      });
      fetchUserDetails();
    } catch (error) {
      toast({
        title: "Error",
        description: "Something went wrong while updating user status.",
        variant: "destructive",
      });
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role?.toLowerCase()) {
      case "sys. admin" || "team admin":
        return "bg-green-500 hover:bg-green-600";
      case "client team":
        return "bg-blue-500 hover:bg-blue-600";
      case "support":
        return "bg-gray-500 hover:bg-gray-600";
      case "client":
        return "bg-purple-500 hover:bg-puple-500";
      default:
        return "bg-slate-500 hover:bg-slate-600";
    }
  };

  const fileTypes = [
    { name: "Documents", ...storageData.document },
    { name: "Images", ...storageData.images },
    { name: "Others", ...storageData.others }
  ];

  const handleNaviage = () => {
    navigate("/manageUsers");
  };

  return (
    <div className="container mx-auto px-3 md:px-4 py-4 md:py-8">
      <div className="mb-16 md:mb-24">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => handleNaviage()}
          className="flex items-center text-gray-500 hover:text-gray-700 mb-16"
        >
          <ArrowLeft className="mr-8 h-4 w-4" />
          <span className="text-sm md:text-base">Back to Users</span>
        </Button>
        <h1 className="text-xl md:text-2xl font-bold text-foreground">User Details</h1>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        <Card className="bg-white dark:bg-[#2c3440] lg:col-span-1">
          <CardHeader className="pb-16 ph-24 pt-24">
            <CardTitle className="text-base md:text-lg">
              Profile Information
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-16 ph-24 pb-24">
            <div className="flex items-center mb-24 md:mb-32">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-slate-200 dark:bg-slate-600 rounded-full flex items-center justify-center text-lg md:text-2xl font-bold text-slate-600 mr-16 dark:text-white flex-shrink-0">
                {userDetails?.name
                  ?.split(" ")
                  ?.map((n) => n[0])
                  ?.join("")
                  ?.toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-lg md:text-xl font-medium truncate mb-8">
                  {userDetails?.name}
                </h3>
                <div className="flex flex-col gap-2">
                  <Badge
                    variant="info"
                    className={`${getRoleBadgeColor(
                      userDetails?.newRole
                    )} text-xs w-fit`}
                  >
                    {userDetails?.newRole?.charAt(0)?.toUpperCase() +
                      userDetails?.newRole?.slice(1)}
                  </Badge>
                  <Badge
                    variant={
                      userDetails?.type === "mobile" ? "secondary" : "outline"
                    }
                    className="text-xs w-fit"
                    style={{ borderRadius: "6px" }}
                  >
                    {userDetails?.type} user
                  </Badge>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div className="border-b border-gray-100 dark:border-gray-700 pb-12">
                <div className="flex items-start">
                  <Mail className="h-5 w-5 mr-12 mt-4 text-slate-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-4">
                      Email
                    </p>
                    <a
                      href={`mailto:${userDetails?.email}`}
                      className="text-blue-600 hover:underline text-sm break-all"
                      style={{ color: "#2563EB" }}
                    >
                      {userDetails?.email}
                    </a>
                  </div>
                </div>
              </div>

              <div className="border-b border-gray-100 dark:border-gray-700 pb-12">
                <div className="flex items-start">
                  <Phone className="h-5 w-5 mr-12 mt-4 text-slate-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-4">
                      Phone
                    </p>
                    <p className="text-sm text-slate-800 dark:text-slate-200">
                      {userDetails?.contact || "Not provided"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-b border-gray-100 dark:border-gray-700 pb-12">
                <div className="flex items-start">
                  <Building className="h-5 w-5 mr-12 mt-4 text-slate-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-4">
                      Company
                    </p>
                    <p className="text-sm text-blue-600 hover:underline">
                      <Link className="hover:text-blue-600" to={`/accountsDetails/${userDetails?.companyId}`}>{userDetails?.company}</Link>
                    </p>
                  </div>
                </div>
              </div>

              <div className="border-b border-gray-100 dark:border-gray-700 pb-12">
                <div className="flex items-start">
                  <Calendar className="h-5 w-5 mr-12 mt-4 text-slate-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-4">
                      Date Created
                    </p>
                    <p className="text-sm text-slate-800 dark:text-slate-200">
                      {userDetails?.createdAt}
                    </p>
                  </div>
                </div>
              </div>

              <div className="pb-12">
                <div className="flex items-start">
                  <Activity className="h-5 w-5 mr-12 mt-4 text-slate-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-4">
                      Last Activity
                    </p>
                    {/* <p className="text-sm text-slate-800">{formatDateTime(userDetails?.lastActivity)}</p> */}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

          <Card className="bg-white dark:bg-[#2c3440] lg:col-span-2 p-24">
            <CardHeader className="pb-16">
              <CardTitle className="flex items-center text-base md:text-lg">
                <HardDrive className="mr-8 h-4 w-4 md:h-5 md:w-5" />
                Storage Usage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-24">
                <StorageUsageGauge 
                  used={storageData?.usedStorage} 
                  total={storageData?.totalStorage} 
                  percentage={storageData?.percentage}
                />
              </div>
              
              <div className="mt-24">
                <h4 className="font-medium mb-16 text-sm md:text-base">Storage Breakdown</h4>
                <div className="space-y-4">
                  {fileTypes?.map((item, index) => (
                    <div
                      key={index}
                      className="flex justify-between items-center p-12 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div className="flex items-center">
                        <div
                          className={`w-3 h-3 rounded-full ${
                            index === 0
                              ? "bg-blue-500"
                              : index === 1
                              ? "bg-green-500"
                              : "bg-orange-500"
                          } mr-12`}
                        ></div>
                        <span className="text-sm font-medium">{item?.name}</span>
                      </div>
                      <div className="flex flex-col items-end space-y-1">
                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {(item?.size / (1024 * 1024 * 1024))?.toFixed(2)} GB
                        </span>
                        <span className="text-xs text-gray-500">{item?.count} files</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

        <Card className="bg-white dark:bg-[#2c3440] lg:col-span-3 p-24">
          <CardHeader className="pb-4">
            <CardTitle className="text-base md:text-lg">
              Account Management
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Status Toggle */}
              <div className="flex items-center justify-between p-16 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div>
                  <h4 className="font-medium">Account Status</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {isActive
                      ? "User account is currently active"
                      : "User account is currently inactive"}
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm">
                    {isActive ? "Active" : "Inactive"}
                  </span>
                  <Switch
                    checked={isActive}
                    onCheckedChange={handleToggleStatus}
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {/* Reset Password */}
                <AlertDialog
                  open={resetPasswordDialog}
                  onOpenChange={setResetPasswordDialog}
                >
                  <AlertDialogTrigger asChild>
                    <Button className="w-full h-12 text-sm">
                      Reset Password
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="mx-4 dark:bg-[#1A1F2C]">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-base md:text-lg dark:text-white">
                        Reset User Password
                      </AlertDialogTitle>
                      <AlertDialogDescription className="text-sm md:text-base dark:text-[#6B7280]">
                        This will send a password reset email to{" "}
                        {userDetails?.email}. The user will need to click the
                        link in the email to create a new password.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                      <AlertDialogCancel className="w-full sm:w-auto">
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleResetPassword}
                        className="w-full sm:w-auto"
                      >
                        Send Reset Email
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                {/* Change Role */}
                <Dialog
                  open={changeRoleDialog}
                  onOpenChange={setChangeRoleDialog}
                >
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full h-12 text-sm">
                      Change Role
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="mx-4 dark:bg-[#1A1F2C]">
                    <DialogHeader className={"dark:bg-[#1A1F2C] dark:text-white"}>
                      <DialogTitle className="text-base md:text-lg dark:text-white">
                        Change User Role
                      </DialogTitle>
                      <DialogDescription className="text-sm md:text-base dark:text-white">
                        {(Array.isArray(userDetails?.role) ? !userDetails.role.includes("Employee") : userDetails?.role !== "Employee")
                          ? `You can not change the role of ${(Array.isArray(userDetails?.role) ? userDetails?.role?.[0] : userDetails?.role)} user`
                          : `Select a new role for ${userDetails?.name}`}
                      </DialogDescription>
                    </DialogHeader>
                    {userDetails?.role === "Employee" && (
                      <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-1 gap-2">
                          {roleOptions?.map((role) => (
                            <Button
                              key={role}
                              variant={
                                selectedRole === role ? "default" : "outline"
                              }
                              className="justify-start text-sm"
                              onClick={() => setSelectedRole(role)}
                            >
                              {role}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                    {(Array.isArray(userDetails?.role) ? userDetails.role.includes("Employee") : userDetails?.role === "Employee") && (
                      <DialogFooter className="flex-col sm:flex-row gap-2 dark:bg-[#1A1F2C]">
                        <Button
                          variant="outline"
                          onClick={() => setChangeRoleDialog(false)}
                          className="w-full sm:w-auto"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleRoleChange}
                          disabled={!selectedRole}
                          className="w-full sm:w-auto"
                        >
                          Update Role
                        </Button>
                      </DialogFooter>
                    )}
                  </DialogContent>
                </Dialog>

                {/* Edit Information */}
                <Dialog
                  open={editInformationDailog}
                  onOpenChange={setEditInformationDailog}
                >
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full h-12 text-sm"  onClick={handleOpenEdit}>
                      <Edit className="mr-8 h-4 w-4" />
                      Edit Information
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="mx-4 dark:bg-[#1A1F2C]">
                    <DialogHeader className={"dark:bg-[#1A1F2C] dark:text-white"}>
                      <DialogTitle className="text-base md:text-lg dark:text-white">
                        Change User Information
                      </DialogTitle>
                      {userDetails?.role !== "Client" && (
                      <DialogDescription className="text-sm md:text-base dark:text-white">
                        {`Edit ${userDetails?.name} Information`}
                      </DialogDescription>
                      )}
                    </DialogHeader>
                    {userDetails?.role !== "Client" ? (
                    <div className="space-y-4 mt-16">
                      <div>
                        <label className="text-sm font-medium dark:text-white">First Name</label>
                        <Input
                          name="firstName"
                          value={formData?.firstName}
                          onChange={handleChange}
                          className="mt-4"
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium dark:text-white">Last Name</label>
                        <Input
                          name="lastName"
                          value={formData?.lastName}
                          onChange={handleChange}
                          className="mt-4"
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium dark:text-white">Email</label>
                        <Input
                          name="email"
                          type="email"
                          value={formData?.email}
                          onChange={handleChange}
                          className="mt-4"
                        />
                      </div>

                      <div>
                        <label className="text-sm font-medium dark:text-white">Phone</label>
                        <Input
                          name="contact"
                          type="tel"
                          value={formData?.contact}
                          onChange={handleChange}
                          className="mt-4"
                        />
                      </div>
                    </div>
                    ) : (
                    <span className="text-sm text-slate-600 dark:text-slate-300">
                      You cannot update this client user’s information here. Please make any changes directly from the Client module.
                    </span>
                    )}

                    {userDetails?.role !== "Client" && (
                      <DialogFooter className="flex-col sm:flex-row gap-2 dark:bg-[#1A1F2C]">
                        <Button
                          variant="outline"
                          onClick={() => setEditInformationDailog(false)}
                          className="w-full sm:w-auto"
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleUpdateInformation}
                          className="w-full sm:w-auto"
                        >
                          Update Information
                        </Button>
                      </DialogFooter>
                    )}
                  </DialogContent>
                </Dialog>

                {/* Delete Account */}
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      className="w-full h-12 text-sm"
                      disabled={userDetails?.status === false}
                    >
                      <Trash2 className="mr-8 h-4 w-4" />
                      Delete Account
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="mx-4 dark:bg-[#1A1F2C]">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-base md:text-lg dark:text-white">
                        Delete User Account
                      </AlertDialogTitle>
                      <AlertDialogDescription className="text-sm md:text-base dark:text-[#6B7280]">
                        Are you sure you want to permanently delete{" "}
                        {userDetails?.name}'s account? This action cannot be
                        undone and will remove all user data.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="flex-col sm:flex-row gap-2">
                      <AlertDialogCancel className="w-full sm:w-auto">
                        Cancel
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={userDetails?.status === false ? undefined : handleDeleteUser}
                        className="w-full sm:w-auto bg-red-600 hover:bg-red-700 color-white"
                      >
                        Delete Account
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserDetails;
